import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { categories, menuData } from '../data/menuData';
import MenuCard from './MenuCard';

// Define subgroups with item IDs instead of names
const spiritSubgroups = {
  rakia: {
    name_bg: 'Ракия',
    name_en: 'Rakia',
    itemIds: ['spirit-1', 'spirit-2', 'spirit-3', 'spirit-4', 'spirit-5', 'spirit-21']
  },
  vodka: {
    name_bg: 'Водка',
    name_en: 'Vodka',
    itemIds: ['spirit-6', 'spirit-7', 'spirit-8']
  },
  gin: {
    name_bg: 'Джин',
    name_en: 'Gin',
    itemIds: ['spirit-9', 'spirit-10']
  },
  whisky: {
    name_bg: 'Уиски',
    name_en: 'Whisky',
    itemIds: ['spirit-11', 'spirit-12', 'spirit-13', 'spirit-14', 'spirit-15']
  },
  anise: {
    name_bg: 'Анасонови напитки',
    name_en: 'Anise Drinks',
    itemIds: ['spirit-16', 'spirit-17', 'spirit-18', 'spirit-19']
  },
  other: {
    name_bg: 'Други',
    name_en: 'Other',
    itemIds: ['spirit-20']
  }
};

// Define subgroups for wines with item IDs
const wineSubgroups = {
  white: {
    name_bg: 'Бяло вино',
    name_en: 'White Wine',
    itemIds: ['wine-1', 'wine-2', 'wine-3']
  },
  red: {
    name_bg: 'Червено вино',
    name_en: 'Red Wine',
    itemIds: ['wine-4', 'wine-5', 'wine-6']
  },
  rose: {
    name_bg: 'Розе',
    name_en: 'Rosé',
    itemIds: ['wine-7']
  },
  whiteDraft: {
    name_bg: 'Наливно бяло/червено вино',
    name_en: 'Draft White/Red Wine',
    itemIds: ['wine-8', 'wine-9']
  }
};

// Define subgroups for hot drinks with item IDs
const hotDrinkSubgroups = {
  coffee: {
    name_bg: 'Кафе',
    name_en: 'Coffee',
    itemIds: ['hot-1', 'hot-2', 'hot-3', 'hot-4', 'hot-5']
  },
  tea: {
    name_bg: 'Чай',
    name_en: 'Tea',
    itemIds: ['hot-6']
  },
  extras: {
    name_bg: 'Добавки',
    name_en: 'Extras',
    itemIds: ['hot-7', 'hot-8']
  }
};

// Define subgroups for beer with item IDs
const beerSubgroups = {
  draft: {
    name_bg: 'Наливна бира',
    name_en: 'Draft Beer',
    itemIds: ['beer-1', 'beer-2', 'beer-3', 'beer-4']
  },
  bottled: {
    name_bg: 'Бутилирана бира',
    name_en: 'Bottled Beer',
    itemIds: [ 'beer-5', 'beer-6', 'beer-7', 'beer-8', 'beer-9', 'beer-10', 'beer-11', 'beer-12', 'beer-13']
  }
};

// Define subgroups for soft drinks with item IDs
const softDrinkSubgroups = {
  juices: {
    name_bg: 'Сокове, лимонади и айрян',
    name_en: 'Juices, Lemonades & Airyan',
    itemIds: ['drink-1', 'drink-2', 'drink-3']
  },
  sodas: {
    name_bg: 'Газирани напитки',
    name_en: 'Sodas',
    itemIds: ['drink-4', 'drink-5', 'drink-6', 'drink-7', 'drink-8', 'drink-9', 'drink-10', 'drink-11', 'drink-12', 'drink-13']
  },
  water: {
    name_bg: 'Вода',
    name_en: 'Water',
    itemIds: ['drink-14']
  }
};

const MenuSection = forwardRef(({ categoryId, searchQuery, activeFilter, onOpenModal }, ref) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  
  const category = categories.find(c => c.id === categoryId);
  const items = menuData[categoryId] || [];

  // Filter items based on search query and active filter
  const filteredItems = items.filter(item => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (language === 'bg' ? item.name_bg : item.name_en).toLowerCase();
      const desc = (language === 'bg' ? item.description_bg : item.description_en).toLowerCase();
      if (!name.includes(query) && !desc.includes(query)) {
        return false;
      }
    }
    
    // Tag filter
    if (activeFilter !== 'all') {
      if (!item.tags || !item.tags.includes(activeFilter)) {
        return false;
      }
    }
    
    return true;
  });

  // Function to render items with subgroups
  const renderGroupedItems = () => {
    let subgroups = null;
    
    // Check if this category has subgroups
    if (categoryId === 'spirits') {
      subgroups = spiritSubgroups;
    } else if (categoryId === 'wines') {
      subgroups = wineSubgroups;
    } else if (categoryId === 'hot_drinks') {
      subgroups = hotDrinkSubgroups;
    } else if (categoryId === 'beer') {
      subgroups = beerSubgroups;
    } else if (categoryId === 'soft_drinks') {
      subgroups = softDrinkSubgroups;
    }
    
    // If no subgroups or filtered items are empty due to search/filter
    if (!subgroups || filteredItems.length === 0) {
      if (filteredItems.length === 0) return null;
      return (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filteredItems.map((item, index) => (
            <MenuCard
              key={item.id}
              item={item}
              index={index}
              onOpenModal={onOpenModal}
            />
          ))}
        </div>
      );
    }
    
    // Group items by subgroup using item IDs
    return Object.entries(subgroups).map(([key, subgroup]) => {
      // Filter items that belong to this subgroup by ID
      const subgroupItems = filteredItems.filter(item => 
        subgroup.itemIds.includes(item.id)
      );
      
      if (subgroupItems.length === 0) return null;
      
      return (
        <div key={key} className="mb-10 last:mb-0">
          <h3 className={`font-cormorant text-xl sm:text-2xl font-semibold mb-4 pb-2 border-b ${
            isDark 
              ? 'text-white/80 border-white/20' 
              : 'text-gray-700 border-gray-200'
          }`}>
            {language === 'bg' ? subgroup.name_bg : subgroup.name_en}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {subgroupItems.map((item, index) => (
              <MenuCard
                key={item.id}
                item={item}
                index={index}
                onOpenModal={onOpenModal}
              />
            ))}
          </div>
        </div>
      );
    });
  };

  if (filteredItems.length === 0 && (searchQuery || activeFilter !== 'all')) {
    return null;
  }

  return (
    <section
      ref={ref}
      id={`section-${categoryId}`}
      data-testid={`menu-section-${categoryId}`}
      className="py-12 sm:py-16"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className={`font-cormorant text-3xl sm:text-4xl font-bold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {language === 'bg' ? category?.name_bg : category?.name_en}
          </h2>
          <div className="w-16 h-1 bg-[#C8102E] mt-3 rounded-full" />
        </motion.div>

        {/* Items Grid with Subgroups */}
        {renderGroupedItems()}
      </div>
    </section>
  );
});

MenuSection.displayName = 'MenuSection';

export default MenuSection;